import type {
  Post,
  Store,
  Topic,
  Location,
  Page,
  PostCard,
  StoreCard,
  AuthorProfile,
  TagInfo,
  SEOMeta,
} from "@/types/content";
import type { ContentType } from "@/config/brand";
import {
  stores,
  topics,
  locations,
  pages,
} from "@/mocks/cmsData";
import { getPostsForContent } from "@/lib/sanityPostsCache";
import {
  buildPostSEO,
  buildStoreSEO,
  buildTopicSEO,
  buildLocationSEO,
  buildAuthorSEO,
  buildTagSEO,
  buildPageSEO,
} from "@/api/seo-builders";

export type { Post, PostCard, Store, StoreCard, Topic, Location, Page, AuthorProfile, TagInfo } from "@/types/content";

/* ---------- Helpers ---------- */

function inferContentType(p: Post): ContentType {
  const title = p.title;
  const tags = p.tags;
  if (title.includes("TOP") || tags.includes("評比") || tags.includes("合集")) {
    return "multiShopReview";
  }
  if (title.includes("懶人包") || tags.includes("懶人包") || tags.includes("攻略")) {
    return "lazyPack";
  }
  if (
    title.includes("指南") ||
    title.includes("怎麼選") ||
    title.includes("入門") ||
    title.includes("第一次") ||
    title.includes("如何選") ||
    title.includes("完整攻略") ||
    tags.includes("指南")
  ) {
    return "guide";
  }
  return "singleShopReview";
}

function toPostCard(p: Post): PostCard {
  return {
    id: p.id,
    slug: p.slug,
    title: p.title,
    excerpt: p.excerpt,
    coverImage: p.coverImage,
    category: p.category,
    contentType: p.contentType || inferContentType(p),
    tags: p.tags,
    author: p.author,
    publishedAt: p.publishedAt,
    readingTime: p.readingTime,
    rating: p.rating,
    reviewCount: p.reviewCount,
  };
}

function toStoreCard(s: Store): StoreCard {
  return {
    id: s.id,
    slug: s.slug,
    name: s.name,
    description: s.description,
    coverImage: s.coverImage,
    category: s.category,
    tags: s.tags,
    priceRange: s.priceRange,
    location: s.location,
    rating: s.rating,
    reviewCount: s.reviewCount,
    features: s.features,
  };
}

/* ---------- Posts ---------- */

export async function getAllPosts(): Promise<PostCard[]> {
  const posts = await getPostsForContent();
  return posts.map(toPostCard);
}

export async function getPostBySlug(slug: string): Promise<Post | null> {
  const posts = await getPostsForContent();
  const post = posts.find((p) => p.slug === slug) || null;
  if (!post) return null;
  return { ...post, seo: buildPostSEO(post) };
}

export async function getPostsByCategory(category: string): Promise<PostCard[]> {
  const posts = await getPostsForContent();
  return posts.filter((p) => p.category === category).map(toPostCard);
}

export async function getRelatedPosts(
  currentSlug: string,
  limit = 3,
): Promise<PostCard[]> {
  const posts = await getPostsForContent();
  return posts.filter((p) => p.slug !== currentSlug).slice(0, limit).map(toPostCard);
}

/* ---------- Home feeds ---------- */

export async function getHotArticles(limit = 6): Promise<PostCard[]> {
  const posts = await getPostsForContent();
  return posts
    .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime())
    .slice(0, limit)
    .map(toPostCard);
}

export async function getEditorsPicks(limit = 4): Promise<PostCard[]> {
  const posts = await getPostsForContent();
  return posts
    .filter((p) => p.rating && p.rating >= 4.5 && p.reviewCount && p.reviewCount >= 50)
    .sort((a, b) => (b.rating || 0) - (a.rating || 0))
    .slice(0, limit)
    .map(toPostCard);
}

export async function getTop10Guides(limit = 6): Promise<PostCard[]> {
  const posts = await getPostsForContent();
  return posts
    .filter((p) => p.title.includes("TOP") || p.tags.some((t) => t === "指南"))
    .sort((a, b) => (b.rating || 0) - (a.rating || 0))
    .slice(0, limit)
    .map(toPostCard);
}

export async function getRealReviews(limit = 6): Promise<PostCard[]> {
  const posts = await getPostsForContent();
  return posts
    .filter((p) => p.rating && p.reviewCount && p.reviewCount >= 30)
    .sort((a, b) => (b.rating || 0) - (a.rating || 0))
    .slice(0, limit)
    .map(toPostCard);
}

export async function getEasyPacks(limit = 6): Promise<PostCard[]> {
  const posts = await getPostsForContent();
  return posts
    .filter((p) => p.tags.some((t) => t === "懶人包" || t === "攻略") && (!p.rating || p.rating < 4.8))
    .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime())
    .slice(0, limit)
    .map(toPostCard);
}

export async function getStylePicks(limit = 4): Promise<PostCard[]> {
  const posts = await getPostsForContent();
  const styleCategories = ["形象管理", "穿搭", "彩妝造型", "色彩鑑定"];
  return posts
    .filter((p) => styleCategories.includes(p.category))
    .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime())
    .slice(0, limit)
    .map(toPostCard);
}

export async function getFeaturedPacks(limit = 3): Promise<PostCard[]> {
  const posts = await getPostsForContent();
  return posts
    .filter((p) => p.rating && p.rating >= 4.5 && p.reviewCount && p.reviewCount >= 80)
    .sort((a, b) => (b.reviewCount || 0) - (a.reviewCount || 0))
    .slice(0, limit)
    .map(toPostCard);
}

/* ---------- Stores ---------- */

export async function getAllStores(): Promise<StoreCard[]> {
  return stores.map(toStoreCard);
}

export async function getStoreBySlug(slug: string): Promise<Store | null> {
  const store = stores.find((s) => s.slug === slug) || null;
  if (!store) return null;
  return { ...store, seo: buildStoreSEO(store) };
}

export async function getStoresByIds(ids: string[]): Promise<StoreCard[]> {
  return stores.filter((s) => ids.includes(s.id)).map(toStoreCard);
}

export async function getStoresByCategory(category: string): Promise<StoreCard[]> {
  return stores.filter((s) => s.category === category).map(toStoreCard);
}

export async function getPopularStores(limit = 3): Promise<StoreCard[]> {
  return stores.sort((a, b) => b.rating - a.rating).slice(0, limit).map(toStoreCard);
}

/* ---------- Cross recommendations ---------- */

export async function getRelatedStoresForPost(
  postCategory: string,
  currentSlug: string,
  limit = 3,
): Promise<StoreCard[]> {
  return stores
    .filter((s) => s.category === postCategory && s.slug !== currentSlug)
    .concat(stores.filter((s) => s.slug !== currentSlug))
    .slice(0, limit)
    .map(toStoreCard);
}

export async function getRelatedPostsForStore(
  storeCategory: string,
  currentSlug: string,
  limit = 3,
): Promise<PostCard[]> {
  const posts = await getPostsForContent();
  return posts
    .filter((p) => p.category === storeCategory && p.slug !== currentSlug)
    .concat(posts.filter((p) => p.slug !== currentSlug))
    .slice(0, limit)
    .map(toPostCard);
}

/* ---------- Topics ---------- */

export async function getAllTopics(): Promise<Topic[]> {
  return topics;
}

export async function getTopicBySlug(slug: string): Promise<Topic | null> {
  const topic = topics.find((t) => t.slug === slug) || null;
  if (!topic) return null;
  return { ...topic, seo: buildTopicSEO(topic) };
}

export async function getPostsByTopicSlug(slug: string): Promise<PostCard[]> {
  const posts = await getPostsForContent();
  const topic = topics.find((t) => t.slug === slug);
  if (!topic) return [];
  return posts.filter((p) => topic.articleIds.includes(p.id)).map(toPostCard);
}

export async function getStoresByTopicSlug(slug: string): Promise<StoreCard[]> {
  const topic = topics.find((t) => t.slug === slug);
  if (!topic) return [];
  return stores.filter((s) => topic.storeIds.includes(s.id)).map(toStoreCard);
}

/* ---------- Locations ---------- */

export async function getAllLocations(): Promise<Location[]> {
  return locations;
}

export async function getLocationBySlug(slug: string): Promise<Location | null> {
  const loc = locations.find((l) => l.slug === slug) || null;
  if (!loc) return null;
  return { ...loc, seo: buildLocationSEO(loc) };
}

export async function getSubLocations(parentSlug: string): Promise<Location[]> {
  return locations.filter(
    (l) => l.slug !== parentSlug && l.slug.startsWith(`${parentSlug}-`),
  );
}

export async function getPostsByLocationSlug(slug: string): Promise<PostCard[]> {
  const posts = await getPostsForContent();
  const loc = locations.find((l) => l.slug === slug);
  if (!loc) return [];
  return posts.filter((p) => loc.articleIds.includes(p.id)).map(toPostCard);
}

export async function getStoresByLocationSlug(slug: string): Promise<StoreCard[]> {
  const loc = locations.find((l) => l.slug === slug);
  if (!loc) return [];
  return stores.filter((s) => loc.storeIds.includes(s.id)).map(toStoreCard);
}

/* ---------- Topic helpers ---------- */

export async function getStoreFeaturesForTopic(slug: string): Promise<Record<string, number>> {
  const topic = topics.find((t) => t.slug === slug);
  if (!topic) return {};
  const topicStores = stores.filter((s) => topic.storeIds.includes(s.id));
  const featureCounts: Record<string, number> = {};
  for (const s of topicStores) {
    for (const f of s.features) {
      featureCounts[f] = (featureCounts[f] || 0) + 1;
    }
  }
  return featureCounts;
}

/* ---------- Pages ---------- */

export async function getPageBySlug(slug: string): Promise<Page | null> {
  const page = pages.find((p) => p.slug === slug) || null;
  if (!page) return null;
  const fallbackDesc = "了解美好時光指南的使命與團隊，我們透過神秘客實地走訪，為你帶來最真實的店家評比。";
  const fallbackKeywords = ["關於我們", "美好時光指南", "美業探店", "神秘客", "團隊介紹"];
  return { ...page, seo: buildPageSEO(page, fallbackDesc, fallbackKeywords) };
}

/* ---------- Search ---------- */

export async function searchContent(query: string): Promise<{
  posts: PostCard[];
  stores: StoreCard[];
}> {
  const posts = await getPostsForContent();
  const lower = query.toLowerCase();
  return {
    posts: posts
      .filter(
        (p) =>
          p.title.toLowerCase().includes(lower) ||
          p.excerpt.toLowerCase().includes(lower) ||
          p.tags.some((t) => t.toLowerCase().includes(lower)) ||
          p.category.toLowerCase().includes(lower),
      )
      .map(toPostCard),
    stores: stores
      .filter(
        (s) =>
          s.name.toLowerCase().includes(lower) ||
          s.description.toLowerCase().includes(lower) ||
          s.tags.some((t) => t.toLowerCase().includes(lower)) ||
          s.category.toLowerCase().includes(lower) ||
          s.location.toLowerCase().includes(lower),
      )
      .map(toStoreCard),
  };
}

/* ---------- Authors ---------- */

function slugifyAuthor(name: string): string {
  const map: Record<string, string> = {
    "Amy編": "xiao-qi-bian",
    "Andy編": "da-qi-bian",
    "神秘客A": "shen-mi-ke-a",
  };
  return map[name] || name.toLowerCase().replace(/\s+/g, "-");
}

export async function getAllAuthors(): Promise<AuthorProfile[]> {
  const posts = await getPostsForContent();
  const authorMap = new Map<string, AuthorProfile>();

  for (const p of posts) {
    if (!p.author) continue;
    const existing = authorMap.get(p.author.id);
    if (existing) {
      existing.articleCount += 1;
      existing.reviewCount += p.reviewCount || 0;
    } else {
      authorMap.set(p.author.id, {
        id: p.author.id,
        slug: slugifyAuthor(p.author.name),
        name: p.author.name,
        avatar: p.author.avatar,
        bio: p.author.bio || "",
        articleCount: 1,
        reviewCount: p.reviewCount || 0,
      });
    }
  }

  return Array.from(authorMap.values());
}

export async function getAuthorBySlug(slug: string): Promise<AuthorProfile | null> {
  const authors = await getAllAuthors();
  const author = authors.find((a) => a.slug === slug) || null;
  if (!author) return null;
  return { ...author, seo: buildAuthorSEO(author) };
}

export async function getPostsByAuthorId(authorId: string): Promise<PostCard[]> {
  const posts = await getPostsForContent();
  return posts.filter((p) => p.author?.id === authorId).map(toPostCard);
}

/* ---------- Tags ---------- */

export async function getAllTags(): Promise<TagInfo[]> {
  const posts = await getPostsForContent();
  const tagMap = new Map<string, TagInfo>();

  for (const p of posts) {
    for (const tag of p.tags) {
      const existing = tagMap.get(tag);
      if (existing) {
        existing.postCount += 1;
      } else {
        tagMap.set(tag, { name: tag, slug: encodeURIComponent(tag), postCount: 1, storeCount: 0 });
      }
    }
  }

  for (const s of stores) {
    for (const tag of s.tags) {
      const existing = tagMap.get(tag);
      if (existing) {
        existing.storeCount += 1;
      } else {
        tagMap.set(tag, { name: tag, slug: encodeURIComponent(tag), postCount: 0, storeCount: 1 });
      }
    }
  }

  return Array.from(tagMap.values()).sort((a, b) =>
    (b.postCount + b.storeCount) - (a.postCount + a.storeCount)
  );
}

export async function getTagBySlug(slug: string): Promise<TagInfo | null> {
  const tags = await getAllTags();
  const tag = tags.find((t) => t.slug === slug) || null;
  if (!tag) return null;
  return { ...tag, seo: buildTagSEO(tag) };
}

export async function getPostsByTag(tagName: string): Promise<PostCard[]> {
  const posts = await getPostsForContent();
  return posts.filter((p) => p.tags.includes(tagName)).map(toPostCard);
}

export async function getStoresByTag(tagName: string): Promise<StoreCard[]> {
  return stores.filter((s) => s.tags.includes(tagName)).map(toStoreCard);
}

/* ---------- SSG / Prerender helpers ---------- */

export type PageEntityType =
  | "post"
  | "store"
  | "topic"
  | "location"
  | "author"
  | "tag"
  | "page";

export async function getAllSlugs(type: PageEntityType): Promise<string[]> {
  switch (type) {
    case "post":
      return (await getAllPosts()).map((p) => p.slug);
    case "store":
      return (await getAllStores()).map((s) => s.slug);
    case "topic":
      return (await getAllTopics()).map((t) => t.slug);
    case "location":
      return (await getAllLocations()).map((l) => l.slug);
    case "author":
      return (await getAllAuthors()).map((a) => a.slug);
    case "tag":
      return (await getAllTags()).map((t) => t.slug);
    case "page":
      return ["about"];
    default:
      return [];
  }
}

export interface PageData {
  entityType: PageEntityType;
  slug: string;
  data: unknown;
  seo: SEOMeta;
}

export async function getPageDataForSlug(
  type: PageEntityType,
  slug: string,
): Promise<PageData | null> {
  switch (type) {
    case "post": {
      const post = await getPostBySlug(slug);
      if (!post) return null;
      return {
        entityType: "post",
        slug,
        data: post,
        seo: post.seo || buildPostSEO(post),
      };
    }
    case "store": {
      const store = await getStoreBySlug(slug);
      if (!store) return null;
      return {
        entityType: "store",
        slug,
        data: store,
        seo: store.seo || buildStoreSEO(store),
      };
    }
    case "topic": {
      const topic = await getTopicBySlug(slug);
      if (!topic) return null;
      return {
        entityType: "topic",
        slug,
        data: topic,
        seo: topic.seo || buildTopicSEO(topic),
      };
    }
    case "location": {
      const loc = await getLocationBySlug(slug);
      if (!loc) return null;
      return {
        entityType: "location",
        slug,
        data: loc,
        seo: loc.seo || buildLocationSEO(loc),
      };
    }
    case "author": {
      const author = await getAuthorBySlug(slug);
      if (!author) return null;
      return {
        entityType: "author",
        slug,
        data: author,
        seo: author.seo || buildAuthorSEO(author),
      };
    }
    case "tag": {
      const tag = await getTagBySlug(slug);
      if (!tag) return null;
      return {
        entityType: "tag",
        slug,
        data: tag,
        seo: tag.seo || buildTagSEO(tag),
      };
    }
    case "page": {
      const page = await getPageBySlug(slug);
      if (!page) return null;
      const fallbackDesc =
        "了解美好時光指南的使命與團隊，我們透過神秘客實地走訪，為你帶來最真實的店家評比。";
      const fallbackKeywords = [
        "關於我們",
        "美好時光指南",
        "美業探店",
        "神秘客",
        "團隊介紹",
      ];
      return {
        entityType: "page",
        slug,
        data: page,
        seo: page.seo || buildPageSEO(page, fallbackDesc, fallbackKeywords),
      };
    }
    default:
      return null;
  }
}